import {
  Square,
  Heading,
  Stack,
  Row,
  Column,
  Copy,
  Button,
  Flex,
} from '@grubhubprod/gh-cookbook-react/dist';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { useParams } from 'react-router-dom';
import { ILocation } from 'src/types/Location';
import {
  ISearchParams,
  SearchStatus,
  useLocationEmployeesSearch,
} from 'src/utilities/useSearchHooks';
import { Pagination } from 'src/components/Pagination/Pagination';
import { Input } from 'src/components/Input/Input';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import { sdk } from 'src/utilities/sdk';
import { pushErrorFromAPI } from 'src/utilities/notifications';
import Table from 'src/components/Table/Table';
import PageSection from 'src/components/enterprise/layout/PageSection';
import { buildCMUIPath, CMUIPage, getHomeBreadcrumb } from 'src/constants/urls';
import { RouterLink } from 'src/components/RouterLink/RouterLink';

interface SearchFormFields {
  employeeQuery: string;
}

export default function EnterpriseLocationEmployees(): JSX.Element {
  const { corpid, locationId } =
    useParams<{ corpid: string; locationId: string }>();

  const currentQueryText = useRef(null);
  const [location, setLocation] = useState<ILocation>(null);
  const { register, handleSubmit } = useForm<SearchFormFields>();

  const {
    doSearch,
    resultsList: employees,
    currentSearchPage,
    firstItemNumberFromPage,
    lastItemNumberFromPage,
    totalPages,
    totalResults,
    hasNoResultsWithoutQuery,
    hasNoResultsWithQuery,
    searchStatus,
  } = useLocationEmployeesSearch(corpid as string, locationId as string);

  useEffect(() => {
    if (corpid && locationId) {
      getLocation();
      doSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpid, locationId]);

  function searchLocationEmployees({ employeeQuery }: SearchFormFields): void {
    const searchParams: ISearchParams = { page: 1 };

    if (employeeQuery) {
      searchParams.query = employeeQuery;
      currentQueryText.current = employeeQuery;
    } else {
      currentQueryText.current = null;
    }

    doSearch(searchParams);
  }

  function getEmployeeCountString(employeeCount: number): string {
    return `${employeeCount} ${employeeCount === 1 ? 'Employee' : 'Employees'}`;
  }

  async function getLocation() {
    try {
      const data: ILocation = await sdk.http.get(
        `/corp-admin/${corpid}/locations/${locationId}`
      );
      setLocation(data);
    } catch (error) {
      pushErrorFromAPI(error);
    }
  }

  if (!location) {
    return null;
  }

  return (
    <Square inset="lg">
      <PageHeader>
        <Stack y="xxx-sm">
          <Heading variant="h1">Employees in {location.name}</Heading>
        </Stack>
        <Breadcrumbs
          breadcrumbs={[
            {
              label: 'Admin home',
              ...getHomeBreadcrumb(corpid),
            },
            {
              label: 'Locations',
              href: `/corporate/enterprise/${corpid}/locations`,
              useRouterLink: true,
            },
          ]}
        />
      </PageHeader>
      <PageSection>
        <Row gutter={24}>
          <Column phone={9} gutter={24}>
            <Stack y="sm">
              <Heading variant="h4">Employees Included in Invoicing</Heading>
            </Stack>
            <Stack y="md">
              <Copy variant="body">
                To add or remove employees, go to the employee's profile page
                under Employee Management.
              </Copy>
            </Stack>
          </Column>
        </Row>
        <Stack y="xxx-sm">
          <Copy variant="body">
            {firstItemNumberFromPage} - {lastItemNumberFromPage} of{' '}
            {getEmployeeCountString(totalResults)}
          </Copy>
        </Stack>
        <Stack y="md" as="form">
          <Row>
            <Column phone={6}>
              <Row gutter={8}>
                <Column gutter={8} phone={8}>
                  <Input
                    ariaLabel="Search locations"
                    name="employeeQuery"
                    id="employeeQuery"
                    ref={register}
                    placeholder="Employee name"
                  />
                </Column>
                <Column gutter={8} phone={4}>
                  <Button
                    variant="primary"
                    block
                    buttonSize="medium"
                    onClick={handleSubmit(searchLocationEmployees)}
                  >
                    Search
                  </Button>
                </Column>
              </Row>
            </Column>
          </Row>
        </Stack>
        <Stack y="md">
          <Table
            isLoading={searchStatus === SearchStatus.LOADING}
            header={() => (
              <Row gutter={12}>
                <Column gutter={12} phone={6}>
                  <Copy variant="body" color="secondaryText">
                    Name
                  </Copy>
                </Column>
                <Column gutter={12} phone={6}>
                  <Copy variant="body" color="secondaryText">
                    Email
                  </Copy>
                </Column>
              </Row>
            )}
            body={() => (
              <>
                {employees.map(
                  ({ first_name, last_name, id, email }, index) => (
                    <Square
                      inset="sm"
                      key={id}
                      borderBottom={
                        index === employees.length - 1 ? 'none' : '1px solid'
                      }
                      borderColor="lightLine"
                    >
                      <Row gutter={12}>
                        <Column gutter={12} phone={6}>
                          <RouterLink
                            href={buildCMUIPath(CMUIPage.EMPLOYEE_DETAILS, {
                              corpId: corpid,
                              employeeId: id,
                            })}
                          >
                            {`${first_name} ${last_name}`}
                          </RouterLink>
                        </Column>
                        <Column gutter={12} phone={6}>
                          {email}
                        </Column>
                      </Row>
                    </Square>
                  )
                )}
                {hasNoResultsWithoutQuery && (
                  <NoResultsMsg message="There are currently no employees. Why don’t you add some?" />
                )}
                {hasNoResultsWithQuery && (
                  <NoResultsMsg
                    message={`No results matching '${currentQueryText.current}'`}
                  />
                )}
              </>
            )}
            showFooter={false}
          />
        </Stack>
        {totalPages > 1 && (
          <Flex justifyContent="center">
            <Pagination
              boundaryLinks
              page={currentSearchPage}
              collectionSize={totalResults}
              pageChange={(page) => {
                const searchParams: ISearchParams = { page };
                if (currentQueryText.current) {
                  searchParams.query = currentQueryText.current;
                }
                doSearch(searchParams);
              }}
            />
          </Flex>
        )}
      </PageSection>
    </Square>
  );
}
